import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  DevicesOther as DevicesIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApi, useAsyncOperation } from '../hooks/useApi';
import { apiService } from '../services/api';
import { User } from '../types';

interface TechnicianAssignment {
  userId: string;
  traccarUserId: number | null;
  name: string;
  email: string;
  status: 'active' | 'inactive';
  assignedDevices?: number;
  lastActivity?: string;
}

interface TechnicianDialogProps {
  open: boolean;
  technician: TechnicianAssignment | null;
  onClose: () => void;
  onSubmit: (data: Partial<TechnicianAssignment>) => void;
  loading: boolean;
}

const TechnicianDialog: React.FC<TechnicianDialogProps> = ({
  open,
  technician,
  onClose,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    traccarUserId: '',
    status: 'active' as 'active' | 'inactive',
  });

  useEffect(() => {
    if (technician) {
      setFormData({
        name: technician.name,
        email: technician.email,
        password: '', // No se requiere al editar
        traccarUserId: technician.traccarUserId?.toString() || '',
        status: technician.status,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        traccarUserId: '',
        status: 'active',
      });
    }
  }, [technician, open]);

  const handleSubmit = () => {
    const submitData: any = {
      name: formData.name, // Usar el nombre tal cual, sin prefijo forzado
      email: formData.email,
      traccarUserId: formData.traccarUserId ? parseInt(formData.traccarUserId) : null,
      status: formData.status,
    };

    // Solo incluir password si es un técnico nuevo
    if (!technician && formData.password) {
      submitData.password = formData.password;
    }

    onSubmit(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {technician ? 'Editar Técnico' : 'Nuevo Técnico'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Nombre del Técnico"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Juan Pérez"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="tecnico@empresa.com"
            />
          </Grid>
          {!technician && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Contraseña"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                helperText="Contraseña de acceso para el técnico"
                required
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Traccar User ID"
              type="number"
              value={formData.traccarUserId}
              onChange={(e) => setFormData({ ...formData, traccarUserId: e.target.value })}
              placeholder="123"
              helperText="ID del usuario en Traccar"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={formData.status}
                label="Estado"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name || !formData.email || (!technician && !formData.password)}
          startIcon={loading ? <CircularProgress size={20} /> : <PersonIcon />}
        >
          {loading ? 'Guardando...' : technician ? 'Actualizar' : 'Crear Técnico'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TechnicianManagement: React.FC = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianAssignment | null>(null);
  const [deviceCounts, setDeviceCounts] = useState<Record<string, number>>({});
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'warning' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  // Load technicians from API
  const {
    data: techniciansData,
    loading: techniciansLoading,
    error: techniciansError,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  const { execute: createTechnician, loading: creating } = useAsyncOperation();
  const { execute: updateTechnician, loading: updating } = useAsyncOperation();
  const { execute: deleteTechnician, loading: deleting } = useAsyncOperation();

  // Load device counts for each technician
  useEffect(() => {
    const users = (techniciansData as any)?.users || [];
    if (users.length === 0) return;
    Promise.all(
      users.map((u: any) =>
        apiService.getTechnicianDevices(u._id)
          .then((res: any) => ({ id: u._id, count: (res?.data?.devices || res?.data || []).length }))
          .catch(() => ({ id: u._id, count: 0 }))
      )
    ).then(results => {
      const counts: Record<string, number> = {};
      results.forEach((r: any) => { counts[r.id] = r.count; });
      setDeviceCounts(counts);
    });
  }, [techniciansData]);

  // Load technicians on component mount
  useEffect(() => {
    console.log('🚀 TechnicianManagement mounted - fetching...');
    fetchTechnicians();
  }, []);

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  // Convert technicians data to TechnicianAssignment format
  const technicians: TechnicianAssignment[] = ((techniciansData as any)?.users || [])
    .map((user: User) => ({
      userId: user._id,
      traccarUserId: (user as any).traccarUserId || null,
      name: user.name,
      email: user.email,
      status: user.status === 'active' ? 'active' : 'inactive',
      assignedDevices: deviceCounts[user._id] || 0,
      lastActivity: (user as any).lastActivity || 'Nunca',
    }));

  const handleCreateTechnician = async (data: Partial<TechnicianAssignment> & { password?: string }) => {
    try {
      const result = await createTechnician(() =>
        apiService.createUser({
          name: data.name!,
          email: data.email!,
          password: data.password!,
          role: 'technician',
          status: data.status!,
          traccarUserId: data.traccarUserId,
        })
      );

      if (result) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Técnico creado exitosamente',
        });
        setDialogOpen(false);
        setSelectedTechnician(null);
        fetchTechnicians();
      }
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Error creando técnico',
      });
    }
  };

  const handleUpdateTechnician = async (data: Partial<TechnicianAssignment>) => {
    if (!selectedTechnician) return;

    try {
      const result = await updateTechnician(() =>
        apiService.updateUser(selectedTechnician.userId, {
          name: data.name!,
          email: data.email!,
          status: data.status!,
          traccarUserId: data.traccarUserId,
        })
      );

      if (result) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Técnico actualizado exitosamente',
        });
        setDialogOpen(false);
        setSelectedTechnician(null);
        fetchTechnicians();
      }
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Error actualizando técnico',
      });
    }
  };

  const handleDeleteTechnician = async (technician: TechnicianAssignment) => {
    if (!window.confirm(`¿Estás seguro de eliminar al técnico ${technician.name}?`)) return;

    try {
      const result = await deleteTechnician(() =>
        apiService.deleteUser(technician.userId)
      );

      if (result) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Técnico eliminado exitosamente',
        });
        fetchTechnicians();
      }
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Error eliminando técnico',
      });
    }
  };

  const handleTechnicianSubmit = (data: Partial<TechnicianAssignment>) => {
    if (selectedTechnician) {
      handleUpdateTechnician(data);
    } else {
      handleCreateTechnician(data);
    }
  };

  const getTechnicianColor = (technicianId: string) => {
    const colors = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B'];
    const index = technicianId.length % colors.length;
    return colors[index];
  };

  return (
    <Box>
      {/* Alert */}
      {alert.show && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Gestión de Técnicos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra los técnicos y sus conexiones con Traccar
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedTechnician(null);
            setDialogOpen(true);
          }}
          size="large"
        >
          Nuevo Técnico
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {technicians.length}
              </Typography>
              <Typography variant="caption">Total Técnicos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {technicians.filter(t => t.status === 'active').length}
              </Typography>
              <Typography variant="caption">Activos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {technicians.filter(t => t.traccarUserId).length}
              </Typography>
              <Typography variant="caption">Con Traccar ID</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                {technicians.filter(t => !t.traccarUserId).length}
              </Typography>
              <Typography variant="caption">Sin Traccar ID</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technicians List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Lista de Técnicos
        </Typography>

        {techniciansLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : techniciansError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error cargando técnicos: {techniciansError}
          </Alert>
        ) : (
          <List>
            {technicians.map((technician, index) => (
              <React.Fragment key={technician.userId}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getTechnicianColor(technician.userId) }}>
                      {technician.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {technician.name}
                        </Typography>
                        <Chip
                          label={technician.status === 'active' ? 'Activo' : 'Inactivo'}
                          color={technician.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                        {!technician.traccarUserId && (
                          <Tooltip title="Sin Traccar ID asignado">
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          📧 {technician.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          🆔 Traccar ID: {technician.traccarUserId || 'No asignado'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📦 Dispositivos asignados: {technician.assignedDevices}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          🕒 Última actividad: {technician.lastActivity}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Ver dispositivos asignados">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => navigate(`/devices?groupType=technician&technicianId=${technician.userId}`)}
                        >
                          <DevicesIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar técnico">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => {
                            setSelectedTechnician(technician);
                            setDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar técnico">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDeleteTechnician(technician)}
                          disabled={deleting}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < technicians.length - 1 && <Box sx={{ mx: 2, my: 1, borderBottom: 1, borderColor: 'divider' }} />}
              </React.Fragment>
            ))}
            {technicians.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No hay técnicos registrados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Crea un nuevo técnico para comenzar
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Paper>

      {/* Technician Dialog */}
      <TechnicianDialog
        open={dialogOpen}
        technician={selectedTechnician}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleTechnicianSubmit}
        loading={creating || updating}
      />
    </Box>
  );
};

export default TechnicianManagement;